import React, { useEffect, useState } from 'react';
import PortalPageHeader from '../../components/portal/PortalPageHeader';
import { useAuth } from '../../hooks/useAuth';
import { updateClientProfile } from '../../services/ClientProfileRepository';
import { usePortalContext } from '../../context/PortalContext';
import { logClientPortalEventOnce } from '../../utils/portalEventLog';
import { ClientForm } from '../../features/clients/components/ClientManager';

export default function ClientProfile() {
  const { currentUser, loading: authLoading } = useAuth();
  const { crmClient, loading: ctxLoading } = usePortalContext();
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  const email = String(currentUser?.email || '').trim();

  useEffect(() => {
    const clientId = String(crmClient?.id || '').trim();
    if (!clientId) return;
    const actorName = String(
      crmClient?.fullName ||
      crmClient?.qualification?.physical?.socialName ||
      crmClient?.qualification?.physical?.name ||
      email || 'Cliente'
    ).trim();
    (async () => {
      try {
        await logClientPortalEventOnce({
          clientId,
          type: 'CLIENT_PORTAL_PROFILE_VIEWED',
          actorName,
          payload: { text: 'Cliente consultou a área de perfil no portal.' },
          storage: 'session',
        });
      } catch (e) { void e; }
    })();
  }, [crmClient?.id]);

  const handleProfileSave = async (payload) => {
    setSaveError('');
    setSaveSuccess(false);
    const docId = String(crmClient?.id || '').trim();
    if (!docId) throw new Error('ID do cliente não encontrado.');
    await updateClientProfile(docId, payload);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 4000);
  };

  if (authLoading || ctxLoading) {
    return (
      <div className="space-y-4">
        <PortalPageHeader title="Minha Conta" subtitle="Consulte os dados oficiais vinculados ao seu cadastro no escritório." badge="Dados cadastrais" />
        <div className="border border-gray-100 bg-white rounded-lg px-3 py-2 flex items-center gap-2">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-200 border-t-[#C5A059]" />
          <span className="text-xs text-gray-500">Carregando...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PortalPageHeader title="Minha Conta" subtitle="Consulte os dados oficiais vinculados ao seu cadastro no escritório." badge="Dados cadastrais" />

      <div className="rounded-lg border border-[#C5A059]/20 bg-[#C5A059]/10 px-3 py-2">
        <div className="text-[11px] text-[#20243B]">Estes são os seus dados oficiais. Campos críticos (CPF, Nome, E-mail, WhatsApp) já preenchidos pelo escritório não podem ser alterados.</div>
      </div>

      {/* Card 'Acesso seguro' — última vez online, conta criada em (signal de segurança + cuidado) */}
      {(() => {
        const fmt = (raw) => {
          if (!raw) return null;
          try {
            const d = new Date(raw);
            if (Number.isNaN(d.getTime())) return null;
            return d.toLocaleString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
          } catch { return null; }
        };
        const lastSignIn = fmt(currentUser?.metadata?.lastSignInTime);
        const created = fmt(currentUser?.metadata?.creationTime);
        if (!lastSignIn && !created) return null;
        return (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 px-3 py-2">
            <div className="flex items-center gap-2 mb-1">
              <i className="fas fa-shield-alt text-emerald-600 text-[10px]" aria-hidden="true" />
              <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wide">Acesso seguro</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
              {lastSignIn ? <div className="text-[11px] text-emerald-800"><span className="font-medium">Último acesso:</span> {lastSignIn}</div> : null}
              {created ? <div className="text-[11px] text-emerald-800"><span className="font-medium">Conta criada:</span> {created}</div> : null}
            </div>
            <div className="text-[10px] text-emerald-600 mt-0.5">Se não reconhece algum acesso, contate o escritório.</div>
          </div>
        );
      })()}

      {saveSuccess && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700">
          ✅ Dados salvos com sucesso!
        </div>
      )}
      {saveError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs text-red-700">{saveError}</div>
      ) : null}

      <ClientForm
        key={String(crmClient?.id || 'profile')}
        initialData={crmClient}
        onSave={handleProfileSave}
        onCancel={() => {}}
        isClientPortal={true}
      />
    </div>
  );
}
